import { Box, Dialog, DialogTitle } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import {
  useProjectLookup,
  useProjectState,
  usePublicProjectLookup,
} from '@client/store/project';
import {
  DialogType,
  ProjectType,
  useEditProjectDialogStore,
} from './edit-project.store';
import EditProjectForm from './EditProjectForm';
import CloseButton from '@ui/CloseButton';
import { projectToProjectInfo } from '@shared/helpers/project.helpers';

const EditProjectDialog: React.FC = () => {
  const {
    project: projectFromStore,
    id,
    type,
    mode,
    reset,
  } = useEditProjectDialogStore();

  const projectLookup = useProjectLookup();
  const publicProjectLookup = usePublicProjectLookup();

  const project = useMemo(() => {
    if (id) {
      return publicProjectLookup[id] ?? projectLookup[id];
    }
    if (projectFromStore) {
      return projectToProjectInfo(projectFromStore);
    }
  }, [projectLookup, publicProjectLookup, id, projectFromStore]);

  const { isCreating } = useProjectState('isCreating');
  const [isSubmitBusy, setIsSubmitBusy] = useState(false);
  // isCreating drops when the row is in the store, before navigate() finishes
  const preventDismiss = isCreating || isSubmitBusy;
  const close = useCallback(() => {
    if (preventDismiss) return;
    reset();
  }, [preventDismiss, reset]);

  if (!mode || !type) {
    return null;
  }

  return (
    <Dialog
      closeAfterTransition
      fullWidth
      open={Boolean(type && mode)}
      onClose={(_event, reason) => {
        if (preventDismiss && reason === 'escapeKeyDown') return;
        close();
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DialogTitle>{getDialogTitle(mode, type)}</DialogTitle>
        <CloseButton onClick={close} disabled={preventDismiss} />
      </Box>
      <EditProjectForm
        mode={mode}
        type={type}
        project={project}
        // Form success must reset even while X / Escape are blocked
        onClose={reset}
        onSubmitBusyChange={setIsSubmitBusy}
      />
    </Dialog>
  );
};

const getDialogTitle = (mode: DialogType, type: ProjectType) => {
  if (mode === 'duplicate') return 'Duplicate project';

  if (mode === 'create')
    return type === 'template' ? 'New project template' : 'New project';

  if (mode === 'edit')
    return type === 'template' ? 'Edit project template' : 'Edit project';
};

export default EditProjectDialog;
